



























// import Vue from 'vue';
import {
  defineComponent,
  onMounted,
  ref,
  computed,
  onBeforeUnmount,
  // watchEffect,
  watch,
} from '@vue/composition-api';
import Mapbox, {MapLayerMouseEvent} from 'mapbox-gl';
// @ts-ignore
import {MglMap, MglNavigationControl} from 'vue-mapbox';
import AmexMapMarker from './amex-map-marker.vue';
// import {useRouter} from '../../../../router';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import {debounce} from 'debounce';

interface ResourceProps {
  name: string;
}

interface PropTypes {
  resource: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon, ResourceProps>[];
  bounds: Mapbox.LngLatBounds;
  selected: string[];
  shownMarkers: string[];
}

interface Marker {
  coordinates: [number, number];
  name: string;
  id: string;
}

interface MapHandler {
  id: string;
  onHover: (e: MapLayerMouseEvent) => void;
  onLeave: () => void;
  onClick: () => void;
}

function useMap(props: PropTypes, emit: (type: string, data?: unknown) => void) {
  let map: Mapbox.Map | null = null;
  const hoverItems = ref<string[]>([]);
  let mapHandlers: MapHandler[] = [];
  const mapBoundsRef = ref(new mapboxgl.LngLatBounds([0, 0], [0, 0]));
  const markers = ref<Marker[]>([]);

  const selectedItem = computed<string[]>(() => {
    return props.selected || [''];
  });
  const shownMarkers = computed<string[]>(() => {
    return props.shownMarkers || [''];
  });

  const isMarkerActive = (markerId: string) => {
    return (
      selectedItem.value.includes(markerId) ||
      hoverItems.value.includes(markerId) ||
      shownMarkers.value.includes(markerId)
    );
  };

  const boundsArrayLike = computed(() => {
    return props.bounds?.toArray() || [0, 0];
  });

  const onMarkerClick = (id: string) => {
    const resource = props.resource.reduce((res: GeoJSON.Feature[], x) => {
      return [...res, ...x.features.filter((y) => y.id === id)];
    }, []);
    hoverItems.value = [];
    emit('select', resource.flat());
  };

  const onMouseMove = (source: string, e: MapLayerMouseEvent) => {
    if (map && e && e.features && e.features.length > 0) {
      hoverItems.value.push(source);
      map.setFeatureState({source, id: 0}, {hover: true});
    }
  };

  const onMouseLeave = (source: string) => {
    if (!map) {
      return;
    }

    mapHandlers.forEach((x) => {
      const xSource = x.id;
      if (xSource && map?.getLayer(xSource)) {
        map.setFeatureState({source: xSource, id: 0}, {hover: false, selected: false});
      }
    });

    selectedItem.value.forEach((x) => {
      if (x && map?.getLayer(x)) {
        map.setFeatureState({source: x, id: 0}, {selected: true});
      }
    });
    hoverItems.value = hoverItems.value.filter((x) => x !== source);
    hoverItems.value.forEach((x) => {
      if (x && map?.getLayer(x)) {
        map.setFeatureState({source: x, id: 0}, {hover: true});
      }
    });
  };

  const filterCoordinatesBasedOnBounds = (
    feature: GeoJSON.Feature<GeoJSON.MultiPolygon, GeoJSON.GeoJsonProperties>,
  ) => {
    const depth = feature.geometry.type === 'MultiPolygon' ? 2 : 1;
    return (feature.geometry.coordinates.flat(depth) as Array<[number, number]>).filter((x) => {
      return mapBoundsRef.value.contains(x) && x[0] > -160 && x[0] < 170;
    });
  };

  const getBoundariesFormCoordinates = (coordinates: Array<[number, number]>) => {
    return coordinates.reduce((sum: mapboxgl.LngLatBounds | null, item) => {
      if (!sum) {
        return new mapboxgl.LngLatBounds(item, item);
      }
      sum.extend(item);
      return sum;
    }, null);
  };

  const updateMarkers = debounce(() => {
    markers.value = props.resource.reduce((allMarkers: Marker[], z) => {
      const features = z.features;
      const featureMarkers: Marker[] = features.reduce((y: Marker[], feature) => {
        const name = feature.properties.name;
        const id = feature.id as string;
        const coordinates = filterCoordinatesBasedOnBounds(feature);
        const result = getBoundariesFormCoordinates(coordinates);

        if (!result) {
          return y;
        }
        const c = result.getCenter();

        const center = [c.lng, c.lat] as [number, number];
        y.push({
          coordinates: center,
          name,
          id,
        });
        return y;
      }, []);
      return [...allMarkers, ...featureMarkers];
    }, []);
    updateMapLayers();
  }, 300);

  const onResize = () => {
    if (map) {
      map.fitBounds(props.bounds);
    }
  };

  const onZoomChanged = () => {
    if (map) {
      mapBoundsRef.value = map.getBounds();
      emit('zoom', map.getZoom());
      emit('bounds-updated', mapBoundsRef.value);
      updateMarkers();
    }
  };

  // let updatePromise: Promise<Array<void>> = Promise.resolve([]);

  const updateMapLayers = () => {
    if (!map) {
      return;
    }

    // await updatePromise;

    mapHandlers.forEach((x) => {
      map?.off('mousemove', x.id, x.onHover);
      map?.off('mouseleave', x.id, x.onLeave);
      map?.off('click', x.id, x.onClick);
      const layer = map?.getLayer(x.id as string);
      if (layer) {
        map?.removeLayer(x.id as string);
      }
      const source = map?.getSource(x.id as string);
      if (source) {
        map?.removeSource(x.id as string);
      }
    });

    mapHandlers = [];

    // updatePromise = Promise.all(
    props.resource.map((resource) => {
      // return new Promise<void>((resolve) => {
      // //@ts-ignore
      // requestIdleCallback(() => {
      resource.features.forEach((x) => {
        if (!map) {
          return;
        }
        const layer = map?.getLayer(x.id as string);
        if (layer) {
          map?.removeLayer(x.id as string);
        }
        const source = map?.getSource(x.id as string);
        if (source) {
          map?.removeSource(x.id as string);
        }

        map?.addSource(x.id as string, {
          type: 'geojson',
          data: {...x, id: x.id},
          generateId: true,
        });

        map?.addLayer({
          id: `${x.id as string}`,
          type: 'fill',
          source: x.id as string,
          paint: {
            'fill-color': '#006FCF',
            'fill-opacity': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              0.5,
              ['boolean', ['feature-state', 'selected'], false],
              1,
              0,
            ],
          },
        });

        const onHover = (e: MapLayerMouseEvent) => onMouseMove(x.id as string, e);
        const onLeave = () => onMouseLeave(x.id as string);
        const onClick = () => onMarkerClick(x.id as string);

        mapHandlers.push({
          id: x.id as string,
          onHover,
          onLeave,
          onClick,
        });

        map?.on('mousemove', x.id as string, onHover);
        map?.on('mouseleave', x.id as string, onLeave);
        map?.on('click', x.id as string, onClick);
      });

      onMouseLeave('');
      // resolve();
      // });
      // });
    });
    // );
  };

  const resources = computed(() => {
    return props.resource;
  });

  const onMapLoaded = async (event: {map: Mapbox.Map}) => {
    map = event.map;
    map.fitBounds(props.bounds || [0, 0]);
    updateMapLayers();
    emit('load', map);
  };

  onMounted(() => {
    window.addEventListener('resize', onResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
  });

  watch(selectedItem, () => {
    onMouseLeave('');
  });
  watch(resources, () => {
    updateMarkers();
  });

  return {
    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
    mapStyle: process.env.VUE_APP_MAPBOX_STYLE,
    onMapLoaded,
    boundsArrayLike,
    markers,
    onMarkerClick,
    selectedItem,
    // center,
    isMarkerActive,
    onZoomChanged,
  };
}

const AmexMap = defineComponent({
  components: {
    MglMap,
    AmexMapMarker,
    MglNavigationControl,
  },
  props: {
    resource: Array,
    bounds: Object,
    selected: Array,
    shownMarkers: Array,
  },
  setup(props, {emit}) {
    return {
      ...useMap(props as PropTypes, emit),
    };
  },
});

export default AmexMap;
